import type {AgentChatEntryPoint} from '@wandb/forge-shell-contract/aria';
import {useCallback, useMemo} from 'react';
import {useSyncExternalStore} from 'use-sync-external-store/shim';

import {
  type AgentChatCopyTarget,
  type AgentChatInteractionPayload,
  type AgentChatMentionKind,
  type AgentChatMentionVia,
  type AgentChatMessageVia,
  type AgentChatStepKind,
  type AgentChatTrackContext,
  getTrackContext,
  type PanelScreenshotAction,
  trackAgentChatInteraction,
} from '../../services/analytics/agentChatEvents';
import {useTrackedEvent} from '../../services/analytics/AnalyticsProvider';
import type {ComposerFile} from '../chat/composer/file';
import {useAriaPageScope} from '../chat/pageContext';
import {useOptionalAgentChatStore} from '../chat/store/context';
import {
  selectActiveThread,
  selectActiveThreadId,
} from '../chat/store/selectors';

/**
 * useTrackWBAgentPanel binds open/close/dock trackers to the current route
 * + chat.
 */
export function useTrackWBAgentPanel() {
  const track = useAgentChatTrack();

  const trackClosed = useCallback(() => {
    track({action: 'closed'});
  }, [track]);

  const trackNavbarButtonViewed = useCallback(() => {
    track({action: 'navbar_button_viewed'});
  }, [track]);

  const trackOpened = useCallback(() => {
    track({action: 'opened'});
  }, [track]);

  return {
    trackClosed,
    trackNavbarButtonViewed,
    trackOpened,
  };
}

/**
 * useTrackWBAgentChats binds chat lifecycle trackers to the current
 * route.
 */
export function useTrackWBAgentChats() {
  const track = useAgentChatTrack();

  const trackChatCreated = useCallback(() => {
    // A brand-new chat has no server identity yet, so clear the active chat_id
    // the tracker would otherwise fill in.
    track({action: 'chat_created', chat_id: undefined});
  }, [track]);

  const trackChatDeleted = useCallback(
    (deletedChatId: string) => {
      track({
        action: 'chat_deleted',
        chat_id: deletedChatId,
      });
    },
    [track]
  );

  const trackChatRenamed = useCallback(
    (renamedChatId: string) => {
      track({
        action: 'chat_renamed',
        chat_id: renamedChatId,
      });
    },
    [track]
  );

  const trackChatTitleGenerated = useCallback(
    (chatId: string) => {
      track({
        action: 'chat_title_generated',
        chat_id: chatId,
      });
    },
    [track]
  );

  const trackChatSwitched = useCallback(
    (nextChatId: string) => {
      track({
        action: 'chat_switched',
        chat_id: nextChatId,
      });
    },
    [track]
  );

  return {
    trackChatCreated,
    trackChatDeleted,
    trackChatRenamed,
    trackChatSwitched,
    trackChatTitleGenerated,
  };
}

/**
 * useTrackWBAgentComposer binds composer-input trackers to the current
 * route + chat.
 */
export function useTrackWBAgentComposer() {
  const track = useAgentChatTrack();

  const trackEntryPointClicked = useCallback(
    (entryPoint: AgentChatEntryPoint) => {
      track({
        action: 'entry_point_clicked',
        entry_point: entryPoint,
      });
    },
    [track]
  );

  const trackMentionAdded = useCallback(
    (via: AgentChatMentionVia, kind: AgentChatMentionKind) => {
      track({
        action: 'mention_added',
        mention_via: via,
        mention_kind: kind,
      });
    },
    [track]
  );

  const trackMentionRemoved = useCallback(
    (kind: AgentChatMentionKind) => {
      track({
        action: 'mention_removed',
        mention_kind: kind,
      });
    },
    [track]
  );

  const trackMessageSent = useCallback(
    (via: AgentChatMessageVia, files: readonly ComposerFile[] = []) => {
      track({
        action: 'message_sent',
        message_via: via,
      });
      const screenshots = files.filter(file => file.screenshotKind);
      if (screenshots.length > 0) {
        track({
          action: 'panel_screenshot_submitted',
          screenshot_count: screenshots.length,
        });
      }
    },
    [track]
  );

  const trackSuggestionClicked = useCallback(
    (label: string) => {
      track({
        action: 'suggestion_clicked',
        suggestion_label: label,
      });
    },
    [track]
  );

  const trackTurnStopped = useCallback(() => {
    track({action: 'turn_stopped'});
  }, [track]);

  return {
    trackEntryPointClicked,
    trackMentionAdded,
    trackMentionRemoved,
    trackMessageSent,
    trackSuggestionClicked,
    trackTurnStopped,
  };
}

/** Screenshot events use the same route, thread and analytics sink as chat. */
export function useTrackWBAgentPanelScreenshot(): (
  payload: Omit<AgentChatInteraction, 'action'> & {
    action: PanelScreenshotAction;
  }
) => void {
  return useAgentChatTrack();
}

/**
 * useTrackWBAgentInspection binds message-action trackers (copy, feedback,
 * step disclosure) to the current route + chat.
 */
export function useTrackWBAgentInspection() {
  const track = useAgentChatTrack();

  const trackCopied = useCallback(
    (target: AgentChatCopyTarget) => {
      track({
        action: 'copied',
        copy_target: target,
      });
    },
    [track]
  );

  const trackFeedbackSubmitted = useCallback(
    (isPositive: boolean | null, hasReasoning: boolean) => {
      track({
        action: 'feedback_submitted',
        feedback_is_positive: isPositive,
        feedback_has_reasoning: hasReasoning,
      });
    },
    [track]
  );

  const trackStepCollapsed = useCallback(
    (kind: AgentChatStepKind, toolName?: string) => {
      track({
        action: 'step_collapsed',
        step_kind: kind,
        tool_name: toolName,
      });
    },
    [track]
  );

  const trackStepExpanded = useCallback(
    (kind: AgentChatStepKind, toolName?: string) => {
      track({
        action: 'step_expanded',
        step_kind: kind,
        tool_name: toolName,
      });
    },
    [track]
  );

  return {
    trackCopied,
    trackFeedbackSubmitted,
    trackStepCollapsed,
    trackStepExpanded,
  };
}

/**
 * An interaction payload without the route and chat scope that
 * `useAgentChatTrack` fills in. Passing a scope field anyway overrides it,
 * which is how `chat_deleted` reports the deleted chat rather than the active
 * one.
 */
type AgentChatInteraction = Omit<
  AgentChatInteractionPayload,
  keyof AgentChatTrackContext
> &
  Partial<AgentChatTrackContext>;

/**
 * useAgentChatTrack is the shared tracker for the domain hooks below. It
 * snapshots route + chat context so every callback ships a consistent payload,
 * and aims the event at whichever app rendered this subtree: the wandb app's
 * own agent panel, or the ARIA slice in the shell header. On a wandb route both
 * are mounted, so an event that took its `app_name` from module state would be
 * attributed to whichever entry registered last.
 */
function useAgentChatTrack(): (payload: AgentChatInteraction) => void {
  const params = useAriaPageScope();
  const chatId = useOptionalResolvedChatId();
  const base = useMemo(
    () => getTrackContext(params.legacyWBMatchParams, chatId),
    [params, chatId]
  );
  const track = useTrackedEvent(trackAgentChatInteraction);

  return useCallback(
    (payload: AgentChatInteraction) => track({...base, ...payload}),
    [base, track]
  );
}

/**
 * useOptionalResolvedChatId returns the active chat's root turn id when known,
 * falling back to the active id (a local UUID before the first turn persists,
 * or the id being loaded mid-switch), or undefined when none is active or the
 * AgentChatStoreContext is missing.
 */
function useOptionalResolvedChatId(): string | undefined {
  // oxlint-disable-next-line wandb/zustand-use-store-selectors - not a zustand store.
  const store = useOptionalAgentChatStore();

  // NOTE: reads the active Thread but returns the resolved id primitive, so the
  // per-streamed-event Thread mutations don't force re-renders.
  const {subscribe, getSnapshot} = useMemo(
    () =>
      store
        ? {
            subscribe: store.subscribe,
            getSnapshot: () => {
              const state = store.getState();
              return (
                selectActiveThread(state)?.id ?? selectActiveThreadId(state)
              );
            },
          }
        : {subscribe: () => () => {}, getSnapshot: () => null},
    [store]
  );

  return useSyncExternalStore(subscribe, getSnapshot) ?? undefined;
}
